import {
    createContext,
    useContext,
    useReducer,
    ReactNode,
    useCallback
} from "react";
import {generateCode} from "../helper/utils";

type NotificationType = "info" | "success" | "warning" | "error";
interface Notification {
    id: string;
    type: NotificationType;
    message: string;
    meta?: {
        actionType?: string
        payload?: any
    }
}
interface State {notifications: Notification[]}
type NotificationProps = {
    message: string,
    type?: NotificationType,
    meta?: {actionType?: string, payload?: any}
}
type Action =
| {type: "ADD_NOTIFICATION"; payload: Notification}
| {type: "REMOVE_NOTIFICATION"; payload: {id: string}}
| {type: "CLEAR_NOTIFICATIONS"};
const Context = createContext<{
    addNotification: (props: NotificationProps) => void
    clearNotifications: () => void
    state: State
    removeNotification: (id: string) => void
} | null>(null)

function notificationReducer(state: State, action: Action): State {
    switch (action.type) {
        case "ADD_NOTIFICATION":
            return {
                notifications: state.notifications.concat([action.payload]),
            };
        case "REMOVE_NOTIFICATION":
            return {
                notifications: state.notifications.filter(
                    (n) => n.id !== action.payload.id
                ),
            };
        case "CLEAR_NOTIFICATIONS":
            return {notifications: []};
        default:
            return state;
    }
};

export function NotificationProvider(props: {children: ReactNode}) {
    const [state, dispatch] = useReducer(notificationReducer, {
        notifications: []
    });
    function addNotification(p: NotificationProps) {
        const id = generateCode(10);
        const notification: Notification = {
            id,
            type: p.type ?? "info",
            message: p.message,
            meta: p.meta
        };
        dispatch({type: "ADD_NOTIFICATION", payload: notification});
    }
    const removeNotification = useCallback(function (id: string) {
        dispatch({type: "REMOVE_NOTIFICATION", payload: {id}});
    }, []);
    const clearNotifications = useCallback(function () {
        dispatch({type: "CLEAR_NOTIFICATIONS"});
    }, []);
    return <Context.Provider
        value={{addNotification, clearNotifications, removeNotification, state}}
    >
        {props.children}
    </Context.Provider>;
}

export const useNotification = () => {
    const context = useContext(Context);
    if (!context) {
        throw new Error("This call  must be used within a NotificationProvider");
    }
    return context;
};
